// What a document FRAGMENT cannot know about itself.
//
// The e-mails view splits a document: the timeline stays in the intro and every
// e-mail becomes its own fragment, rendered by its own `MarkdownView`. Two
// facts then live outside the fragment being rendered:
//
//   - the `<seg>` declarations (the legend sits in the intro, the colours are
//     used inside the folds), and
//   - which fold ids exist (the timeline is in the intro, the folds are not).
//
// Deriving either from the fragment gives the wrong answer: segment chips would
// print their raw key, and every timeline beat would look like it points at
// nothing and render as dead text. So the splitter builds this once from the
// WHOLE source and hands it to each fragment.

import { docElements, emailCardId, parseDocHtml, phaseCardId, safeDocId, type DocNode } from "./doc-parse";
import { collectSegments, type SegmentMap } from "./doc-segments";

export interface DocContext {
  segments: SegmentMap;
  /**
   * Ids a jump can land on: `<fold id="...">` plus every `<email n>` card
   * (`email-01`). The campaign map's `<tl>` rows and inline `<ref to>` links
   * answer "does my target exist" against this one list, the same law the
   * timeline beats already obey.
   */
  foldIds: string[];
}

export function docContextFromNodes(nodes: DocNode[]): DocContext {
  return {
    segments: collectSegments(nodes),
    foldIds: docElements(nodes)
      .map((node) => (
        node.tag === "fold" ? safeDocId(node.attrs.id)
          : node.tag === "email" ? emailCardId(node.attrs.n)
            : node.tag === "ph" ? phaseCardId(node.attrs.n)
              : node.tag === "wireframe" ? safeDocId(node.attrs.id)
                : undefined
      ))
      .filter((id): id is string => id !== undefined)
  };
}

export function buildDocContext(source: string): DocContext {
  return docContextFromNodes(parseDocHtml(source));
}
